import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";
import { PILLARS } from "@/data/articles";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const BASE = "https://vcclaychurch.com";

export default function ArticlePage({ article }) {
  if (!article) return null;
  const pillar = PILLARS[article.pillar];
  const path = `/${article.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.metaTitle,
        description: article.description,
        url: `${BASE}${path}`,
        mainEntityOfPage: `${BASE}${path}`,
        author: { "@type": "Organization", name: "Victory Chapel Clay", url: BASE },
        publisher: { "@type": "Organization", name: "Victory Chapel Clay", url: BASE },
        isPartOf: { "@type": "WebSite", name: "Victory Chapel Clay", url: BASE },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Resources", item: `${BASE}/resources` },
          { "@type": "ListItem", position: 3, name: article.metaTitle },
        ],
      },
      ...(article.faqs?.length
        ? [{
            "@type": "FAQPage",
            mainEntity: article.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }]
        : []),
    ],
  };

  return (
    <>
      <SEO title={article.metaTitle} description={article.description} path={path} jsonLd={jsonLd} />

      <section className="grain relative overflow-hidden bg-navy py-16 text-white sm:py-24">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-navy-light/50 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[360px] w-[360px] rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/60" data-testid="breadcrumb">
            <Link to="/" className="transition-colors hover:text-white">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <Link to="/resources" className="transition-colors hover:text-white">Resources</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page" className="text-white/85">{article.metaTitle}</span>
          </nav>
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">{pillar?.name || "Resources"}</p>
          <LineReveal className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tighter sm:text-6xl" lines={article.headline} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg" data-testid="article-intro">{article.intro}</p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-white/50" data-testid="article-byline">
              Pastoral resources from Victory Chapel Clay &middot; Middleburg, FL
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="article-body">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          {article.sections.map((sec, i) => (
            <Reveal key={i} className={i > 0 ? "mt-12" : ""}>
              <article data-testid={`article-section-${i}`}>
                <h2 className="text-balance font-display text-2xl font-black tracking-tight text-navy sm:text-3xl">{sec.h2}</h2>
                {sec.answer && (
                  <p className="mt-4 rounded-2xl border-l-4 border-victory bg-cream px-5 py-4 text-base font-medium leading-relaxed text-navy sm:text-lg" data-testid={`article-answer-${i}`}>
                    {sec.answer}
                  </p>
                )}
                {sec.body.map((p, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{p}</p>
                ))}
                {sec.list && (
                  <ul className="mt-4 space-y-3">
                    {sec.list.map((li, j) => (
                      <li key={j} className="flex gap-3 text-base leading-relaxed text-slate-600 sm:text-lg">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-victory" aria-hidden="true" /> {li}
                      </li>
                    ))}
                  </ul>
                )}
                {sec.verse && (
                  <figure className="mt-6 rounded-2xl bg-navy px-6 py-5 text-white">
                    <blockquote className="font-serif text-lg italic leading-relaxed sm:text-xl">"{sec.verse.text}"</blockquote>
                    <figcaption className="mt-2 font-display text-xs font-700 uppercase tracking-[0.25em] text-rose-300">{sec.verse.ref}</figcaption>
                  </figure>
                )}
              </article>
            </Reveal>
          ))}

          {article.pastorIntro && (
            <Reveal className="mt-12">
              <div className="rounded-3xl bg-navy p-7 text-white sm:p-9" data-testid="article-pastor-intro">
                <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-rose-300">A note from Pastor Daniel</p>
                <p className="mt-3 font-serif text-lg italic leading-relaxed text-white/90">{article.pastorIntro}</p>
                <p className="mt-3 text-sm text-white/60">Pastor Daniel Richardson &middot; Victory Chapel Clay</p>
              </div>
            </Reveal>
          )}

          <Reveal className="mt-12">
            <p className="border-t border-navy/10 pt-6 text-sm leading-relaxed text-slate-500" data-testid="article-attribution">
              From <Link to="/about" className="font-semibold text-victory transition-colors hover:text-victory-dark">Victory Chapel Clay</Link> -
              a new Christian church at 3180 CR 220 STE 6-7, Middleburg, FL, launching January 2027.
              You're invited to <Link to="/im-new" className="font-semibold text-victory transition-colors hover:text-victory-dark">plan your first visit</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {article.faqs?.length > 0 && (
        <section className="bg-cream py-14 sm:py-20" data-testid="article-faq-section">
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">COMMON QUESTIONS</h2>
            </Reveal>
            <Reveal delay={0.12}>
              <Accordion type="single" collapsible className="mt-8" data-testid="article-faq-accordion">
                {article.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`af-${i}`} className="border-navy/10">
                    <AccordionTrigger data-testid={`article-faq-trigger-${i}`} className="text-left font-display text-lg font-700 text-navy hover:text-victory">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-navy py-14 text-white sm:py-20" data-testid="article-cta-section">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <HeartHandshake size={32} className="mx-auto text-victory" aria-hidden="true" />
            <h2 className="mt-5 text-balance font-display text-3xl font-black tracking-tighter sm:text-4xl">
              YOU DON'T HAVE TO WALK THROUGH THIS ALONE.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/75">
              Whatever brought you to this page today, there's a next step - and we'd be honored to take it with you.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3.5">
              <Link to={article.cta.primary.path} data-testid="article-cta-primary"
                    className="inline-flex items-center gap-2 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                {article.cta.primary.label} <ArrowRight size={15} aria-hidden="true" />
              </Link>
              {article.cta.secondary.map((c, i) => (
                <Link key={c.path} to={c.path} data-testid={`article-cta-secondary-${i}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-700 text-white transition-colors duration-300 hover:bg-white/10">
                  {c.label}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-16" data-testid="article-related-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-victory">Keep reading</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              {article.related.map((r) => (
                <Link key={r.path} to={r.path} data-testid={`article-related-${r.path.replace(/\//g, "-")}`}
                      className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-cream p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                  <h3 className="font-display text-lg font-800 tracking-tight text-navy group-hover:text-victory">{r.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{r.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-display text-xs font-700 text-victory">
                    READ <ArrowRight size={13} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
